import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  template: `
    <style>
      .demo-dialog .columns {
        display: flex;
        flex-direction: row;
      }

      .demo-dialog .columns > * {
        padding: 0.5em 1em;
      }

      .demo-dialog h3 {
        margin-bottom: 0.25em;
      }

      .demo-dialog .n-maps {
        padding: 0.5em 1em;
        flex-direction: row-reverse;
        border: 1px solid #999;
        float: right;
      }

      .demo-dialog .mat-tab-body-content {
        padding: 1em 0;
      }
    </style>

    <div [ngSwitch]="data.step" class="demo-dialog">
      <div *ngSwitchCase="1" class="columns">
        <markdown [data]="md.step1a"> </markdown>
        <markdown [data]="md.step1b"> </markdown>
      </div>
      <markdown *ngSwitchCase="2" [data]="md.step2"> </markdown>
      <markdown *ngSwitchCase="3" [data]="md.step3"> </markdown>
      <markdown *ngSwitchCase="4" [data]="md.step4"> </markdown>
      <markdown *ngSwitchCase="5" [data]="md.step5"> </markdown>
      <mat-tab-group *ngSwitchCase="6" [dynamicHeight]="true">
        <mat-tab label="Aquatic"
          ><p class="n-maps">{{ md.step6.aquatic.nMaps }} maps</p>
          <markdown [data]="md.step6.aquatic.md"> </markdown
        ></mat-tab>
        <mat-tab label="Lichens"
          ><p class="n-maps">{{ md.step6.lichens.nMaps }} maps</p>
          <markdown [data]="md.step6.lichens.md"> </markdown
        ></mat-tab>
        <mat-tab label="Trees"
          ><p class="n-maps">{{ md.step6.trees.nMaps }} maps</p>
          <markdown [data]="md.step6.trees.md"> </markdown
        ></mat-tab>
      </mat-tab-group>
      <markdown *ngSwitchCase="7" [data]="md.step7"> </markdown>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class DemoDialogComponent {
  readonly md = {
    step1a: `
### Areas to Ultimately Include:
- Class 1 Areas
- Federal Admin Areas
- Tribal Lands
- Wilderness Areas
- Level 1-3 Ecoregions
- ECS
- _BLM_

### Ways to Select Your Area:
1. Select from dropdown list
2. Type name into search bar
3. Click on the area on the map

    `,
    step1b: `
### Order of Implementation:
1. Class 1, 1-3 Ecoregions
2. Subset of Admin Areas (NPS)
3. Wilderness Areas, Tribal Lands
4. Other Admin Areas (FS & FWS)
5. ECS
6. _Way later: BLM areas_
7. _Possibly someday: States_
    `,
    step2: `
### Aquatic
- Sensitive Lakes

### Lichens
- Total Species Richness
- Sensitive-Species Richness
- Forage—Abundance
- Cyano—Abundance
- Composite Score

### Trees
- 94 Horn Tree Species
    `,
    step3: `
### Deposition Data
- Ultimately Include:
    - Upcoming TDep & CMAQ updated layers
- Initially Include:
    - T-Dep tw (total deposition) for 2014-2016 three-year average (for Aquatic & Trees)
    - CMAQ v. 5.0.2. (for Lichens)
    `,
    step4: `
### Aquatic
- Single CL (varying methods & uncertainty)

### Lichens
- Total species richness CL
- Sensitive-species richness CL
- Forage—abundance CL
-  Cyano—abundance CL
- Composite score CL

### Trees
- Growth Rate CL
- Survival Rate CL

    `,
    step5: `
### Tree Species
- Select Horn species of interest
- In the final tool, this list will only include the subset of Horn species that are present in the user’s selected area
- Final tool will include scientific and common names

    `,
    step6: {
      aquatic: {
        nMaps: '2',
        md: `
1. Single CL (varying methods & uncertainty)
2. EXC
`,
      },
      lichens: {
        nMaps: '15',
        md: `
1. CL-by lichen type
    - Total species richness
    - Sensitive-species richness
    - Forage—abundance
    - Cyano—abundance
    - Composite score
2. EXC- of CL
    - Total species richness
    - Sensitive-species richness
    - Forage—abundance
    - Cyano—abundance
    - Composite score
3. Current condition—(% decline of species richness from N Dep)
    - Total species richness
    - Sensitive-species richness
    - Forage—abundance
    - Cyano—abundance
    - Composite score
`,
      },
      trees: {
        nMaps: '~1,200',
        md: `
_(for N & S deposition; growth & survival → x4)_

1. Combined CL
    - Min
    - Median
    - _95th percentile (once it becomes available)_
2. Combined EXC
    - Min
    - Median
    - _95th percentile (once it becomes available)_
3. Individual Species EXC
4. _Combined Current Condition (by plot)_
    - _Current rate of Growth/Survival_
    - _% decrease in Growth/Survival rate_
    - _% of species experiencing detrimental effects (EXC)_
    - _% of basal area in EXC_
5. _Individual Species Current Condition (by plot)_
    - _Current rate of Growth/Survival_
    - _% decrease in Growth/Survival rate_
    - _Decrease in basal area or biomass_
    `,
      },
    },
    step7: `
### Maps

_(Multiple outputs will be downloaded in the format of a .zip folder including a metadata file that explains the naming structure of the included files)_

1. Image Download (maps in JPEG format)
2. Data Download (maps in raster/shapefile format)

### Figures

1. N-CLAS will link to Mike Bell’s RShiny reports so that users can access everything from within N-CLAS (one-stop shop)
2. May include figures from the original N-CLAS:
    - Species bar charts
    - Site comparison bar charts
    - Color-coded impacts table
    `,
  };

  constructor(@Inject(MAT_DIALOG_DATA) public data: { step: number }) {}
}
